/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.querySelector('.thb-quick-search');
    this.button = document.getElementById('quick-search');
    this.close_button = this.container.querySelector('.thb-search-close');
    this.input = this.container.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.container.querySelector('.thb-quick-search--results');

    this.setupEventListeners();
    this.setupPredictiveTabsDelegation();
  }

  setupEventListeners() {
    const form = this.container.querySelector('form.searchform');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      const headerSection = document.querySelector('.header-section');
      if (!headerSection) {
        return false;
      }
      if (headerSection.classList.contains('search-open')) {
        this.close();
      } else {
        headerSection.classList.add('search-open');
        setTimeout(() => {
          if (this.input) {
            this.input.focus({
              preventScroll: true
            });
          }
        }, 100);
        dispatchCustomEvent('search:open');
      }

      return false;
    });

    // Close (must stack above fixed #header — see search-overlay.css)
    if (this.close_button) {
      this.close_button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      });
    }
    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        this.close();
      }
    });
  }

  setupPredictiveTabsDelegation() {
    if (!this.predictiveSearchResults) {
      return;
    }
    this.predictiveSearchResults.addEventListener('click', this.onPredictiveResultsClick.bind(this));
    this.predictiveSearchResults.addEventListener('keydown', this.onPredictiveResultsKeydown.bind(this));
  }

  onPredictiveResultsClick(event) {
    const tab = event.target.closest('.search-categories [role="tab"]');
    if (!tab || !this.predictiveSearchResults.contains(tab)) {
      return;
    }
    event.preventDefault();
    this.activatePredictiveSearchTab(tab, { focusPanel: true });
  }

  onPredictiveResultsKeydown(event) {
    if (event.target.getAttribute('role') !== 'tab') {
      return;
    }
    const tablist = event.target.closest('[role="tablist"]');
    if (!tablist || !this.predictiveSearchResults.contains(tablist)) {
      return;
    }
    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    if (!tabs.length) {
      return;
    }
    const index = tabs.indexOf(event.target);
    if (index === -1) {
      return;
    }

    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';
    const prevKey = rtl ? 'ArrowRight' : 'ArrowLeft';

    switch (event.key) {
      case nextKey:
      case 'ArrowDown':
        event.preventDefault();
        tabs[(index + 1) % tabs.length].focus();
        break;
      case prevKey:
      case 'ArrowUp':
        event.preventDefault();
        tabs[(index - 1 + tabs.length) % tabs.length].focus();
        break;
      case 'Home':
        event.preventDefault();
        tabs[0].focus();
        break;
      case 'End':
        event.preventDefault();
        tabs[tabs.length - 1].focus();
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        this.activatePredictiveSearchTab(event.target, { focusPanel: true });
        break;
      default:
        break;
    }
  }

  activatePredictiveSearchTab(tab, options = {}) {
    const { focusPanel = false } = options;
    const tablist = tab.closest('[role="tablist"]');
    if (!tablist || !this.container.contains(tablist)) {
      return;
    }
    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    const panelId = tab.getAttribute('aria-controls');
    let panel = panelId ? document.getElementById(panelId) : null;
    if (panel && !this.container.contains(panel)) {
      panel = null;
    }

    tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.classList.toggle('active', selected);
      t.tabIndex = selected ? 0 : -1;
      if (selected) {
        t.setAttribute('aria-current', 'true');
      } else {
        t.removeAttribute('aria-current');
      }
    });

    this.container.querySelectorAll('.search-results-container [role="tabpanel"]').forEach((section) => {
      const match = panel && section === panel;
      section.classList.toggle('active', match);
      if (match) {
        section.removeAttribute('hidden');
      } else {
        section.setAttribute('hidden', '');
      }
    });

    if (focusPanel && panel && typeof panel.focus === 'function') {
      requestAnimationFrame(() => {
        panel.focus({ preventScroll: true });
      });
    }
  }

  setComboboxExpanded(expanded) {
    if (!this.input || this.input.getAttribute('role') !== 'combobox') {
      return;
    }
    this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  hasSearchResultsMarkup() {
    if (!this.predictiveSearchResults) {
      return false;
    }
    const root = this.predictiveSearchResults.querySelector('#predictive-search-results');
    if (!root) {
      return false;
    }
    return !!root.querySelector('.search-results');
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
        this.predictiveSearchResults.classList.remove('active');
      }
      this.setComboboxExpanded(false);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (!this.predictiveSearchResults) {
      return;
    }

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    this.predictiveSearchResults.classList.add('loading');

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
      .then((response) => {
        this.predictiveSearchResults.classList.remove('loading');
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.classList.remove('loading');
        }
        this.setComboboxExpanded(false);
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      return;
    }
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    const hasResults = this.hasSearchResultsMarkup();
    if (hasResults) {
      this.predictiveSearchResults.classList.add('active');
    } else {
      this.predictiveSearchResults.classList.remove('active');
    }
    this.setComboboxExpanded(hasResults);
  }

  open() {
    document.querySelector('.header-section').classList.add('search-open');
  }

  close() {
    const headerSection = document.querySelector('.header-section');
    if (!headerSection || !headerSection.classList.contains('search-open')) {
      return;
    }
    headerSection.classList.remove('search-open');
    this.setComboboxExpanded(false);
    const trigger = this.button;
    if (trigger && typeof trigger.focus === 'function') {
      requestAnimationFrame(() => {
        trigger.focus({ preventScroll: true });
      });
    }
  }
}
window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') {
    new PredictiveSearch();
  }
});
